import React, { useMemo } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import rehypeRaw from "rehype-raw";
// TODO(hassanahmad): [NX-12930] Review and remove legacy sanitizeInput HTML sanitization layer
import rehypeSanitize from "rehype-sanitize";
import type { PluggableList } from "unified";

import { cn, CommandType, formatInstructionCommandsV2, preprocessContent } from "@/utils";
import markdownSanitizeSchema from "@/utils/markdownSanitizeSchema";

import CustomCommandRenderer from "./CustomCommandRenderer";

interface IProcessContent {
  content: string;
  isCommandBarVisible: boolean;
}

interface IMarkdownProps {
  children: string;
  classNames?: {
    em?: string;
    para?: string;
    textPrimary?: string;
    code?: string;
    commandBar?: string;
  };
  isCommandBarVisible?: boolean;
}

const COMMAND_BAR_DIV_ARIA_LABEL = "commands";
const COMMAND_BAR_HAS_CLEAN_CONTENT_ATTR = "data-has-clean-content";
const MARKDOWN_REHYPE_PLUGINS: PluggableList = [rehypeRaw, [rehypeSanitize, markdownSanitizeSchema]];

const HTML_JS_TAG_PATTERN = RegExp(/<\/?[a-z][\s\S]*>/i);

const convertNewlinesToMarkdown = (rawContent: string) => {
  // Trim leading/trailing spaces from each line, then convert newlines
  return rawContent
    .replace(/^[ \t]+/gm, "")
    .replaceAll(/\n/g, "  \n")
    .replaceAll(/\\n/gi, "  \n");
};

// TODO(hassanahmad): [NX-12930] Review and remove legacy sanitizeInput HTML sanitization layer
const sanitizeInput = (input: string) => {
  if (!HTML_JS_TAG_PATTERN.test(input)) {
    return input;
  }
  return input.replace(/<[^>]*>/g, "");
};
const processContent = ({ content, isCommandBarVisible }: IProcessContent) => {
  content = sanitizeInput(content.trim());
  content = preprocessContent(content);

  const result = formatInstructionCommandsV2(content);
  // if we have commands and isCommandBarVisible is true, then add command bar.
  if (result.hasCommands && isCommandBarVisible) {
    const hasCleanContent = !!result.cleanContent;

    // Escape backticks
    const escapedCleanContent = result.cleanContent ? result.cleanContent.replace(/`/g, "&#96;") : "";

    content =
      escapedCleanContent +
      (escapedCleanContent ? "\n\n" : "") +
      `<div aria-label="${COMMAND_BAR_DIV_ARIA_LABEL}" ${COMMAND_BAR_HAS_CLEAN_CONTENT_ATTR}="${hasCleanContent}">${result.commandsContent}</div>`;
  }

  content = convertNewlinesToMarkdown(content);
  return content;
};

const Markdown = ({ children, classNames, isCommandBarVisible = false }: IMarkdownProps) => {
  const shouldShowCommandBar = isCommandBarVisible;

  const components = useMemo<Components>(() => {
    const renderHeading: Components["h1"] = ({ children, node }) => {
      const headingTagName = (node?.tagName ?? "h1") as keyof React.JSX.IntrinsicElements;
      return React.createElement(headingTagName, { className: cn(classNames?.textPrimary) }, children);
    };

    return {
      em: ({ children }) => <em className={cn("italic leading-6", classNames?.em)}>{children}</em>,
      p: ({ children }) => <span className={cn("leading-6 mb-[10px] last:mb-0", classNames?.para)}>{children}</span>,
      ul: ({ children }) => <ul className={cn(classNames?.textPrimary)}>{children}</ul>,
      code: ({ children }) => (
        <blockquote className={cn("bg-colorHighlight max-w-max px-1 rounded-md text-black", classNames?.code)}>{children}</blockquote>
      ),
      h1: renderHeading,
      h2: renderHeading,
      h3: renderHeading,
      h4: renderHeading,
      h5: renderHeading,
      h6: renderHeading,
      q: ({ children }) => <q className={cn(classNames?.textPrimary)}>{children}</q>,
      aside: (props) =>
        shouldShowCommandBar ? (
          <CustomCommandRenderer role={props.role as CommandType} className={classNames?.commandBar}>
            {props.children}
          </CustomCommandRenderer>
        ) : (
          <aside role={props.role}>{props.children}</aside>
        ),
      div: ({ className, children, ...props }) => {
        const isCommandsBarDiv = props["aria-label"] === COMMAND_BAR_DIV_ARIA_LABEL;
        if (isCommandsBarDiv && shouldShowCommandBar) {
          const hasCleanContent = props[COMMAND_BAR_HAS_CLEAN_CONTENT_ATTR as keyof typeof props] === "true";
          return (
            <div className={cn("flex flex-col text-sm gap-md", hasCleanContent && "pt-[13px] border-0 border-t border-solid border-t-white/[0.26]")}>
              {children}
            </div>
          );
        }
        return (
          <div className={className} {...props}>
            {children}
          </div>
        );
      },
    };
  }, [classNames, shouldShowCommandBar]);

  if (!children) return <></>;

  return (
    <ReactMarkdown rehypePlugins={MARKDOWN_REHYPE_PLUGINS} components={components}>
      {processContent({ content: children, isCommandBarVisible })}
    </ReactMarkdown>
  );
};

export default Markdown;
